import { Ard } from "@amzn/fuse-accounting-central-datastore-client";
import { load, remove, save } from "src/commons/localStorage";
import { ModalMode } from "src/context/jem-gen/types";

/**
 * Storage key prefix for ARD drafts persisted in browser localStorage.
 *
 * Keys use the following shape:
 *   - CREATE     -> bart.jemGen.ardDraft.create
 *   - DUPLICATE  -> bart.jemGen.ardDraft.duplicate.<sourceArdId>
 *   - EDIT       -> bart.jemGen.ardDraft.edit.<ardId>
 */
const DRAFT_KEY_PREFIX = "bart.jemGen.ardDraft";

/** Drafts older than this are evicted on load. */
export const DRAFT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/** Wrapped payload stored in localStorage. */
export interface ArdDraftPayload {
  /** ISO-8601 timestamp of when the draft was last saved. */
  savedAt: string;
  /**
   * For EDIT/DUPLICATE drafts: the `versionId` of the base ARD at the moment
   * the draft was created. Used to evict drafts whose underlying ARD has been
   * updated server-side since the draft was made.
   */
  baseVersionId?: number;
  /** The in-progress ARD form data. */
  formData: Ard;
}

/**
 * Build the localStorage key used to persist the draft for the given modal
 * mode + (optionally) the base ARD being edited / duplicated.
 *
 * Returns `null` when we don't have enough information to build a stable key
 * (e.g., DUPLICATE/EDIT mode without an underlying ARD), in which case
 * draft persistence should be skipped.
 */
export const getDraftKey = (
  modalMode: ModalMode,
  baseArd: Ard | null,
): string | null => {
  switch (modalMode) {
    case ModalMode.CREATE:
      return `${DRAFT_KEY_PREFIX}.create`;
    case ModalMode.DUPLICATE:
      return baseArd?.id ? `${DRAFT_KEY_PREFIX}.duplicate.${baseArd.id}` : null;
    case ModalMode.EDIT:
      return baseArd?.id ? `${DRAFT_KEY_PREFIX}.edit.${baseArd.id}` : null;
    default:
      return null;
  }
};

/**
 * Load a draft from localStorage, automatically evicting it if it is stale
 * (mismatched `versionId` or older than {@link DRAFT_MAX_AGE_MS}).
 *
 * @returns the draft's `formData` if a valid draft exists, otherwise `null`.
 */
export const loadDraft = (
  key: string | null,
  baseArd: Ard | null,
): { formData: Ard; savedAt: string } | null => {
  if (!key) return null;

  const payload = load(key) as ArdDraftPayload | undefined | null;
  if (!payload || !payload.formData || !payload.savedAt) {
    return null;
  }

  // Evict drafts older than the max age.
  const savedAtMs = Date.parse(payload.savedAt);
  if (Number.isFinite(savedAtMs) && Date.now() - savedAtMs > DRAFT_MAX_AGE_MS) {
    remove(key);
    return null;
  }

  // Evict stale drafts (server-side ARD has been updated since the draft was
  // made, so we must not silently restore a now-out-of-date copy).
  if (
    baseArd &&
    payload.baseVersionId !== undefined &&
    baseArd.versionId !== undefined &&
    payload.baseVersionId !== baseArd.versionId
  ) {
    remove(key);
    return null;
  }

  return { formData: payload.formData, savedAt: payload.savedAt };
};

/**
 * Save a draft to localStorage. Returns the ISO timestamp it was saved at,
 * or `null` if persistence was skipped (e.g., key not available).
 */
export const saveDraft = (
  key: string | null,
  formData: Ard,
  baseArd: Ard | null,
): string | null => {
  if (!key) return null;

  const savedAt = new Date().toISOString();
  const payload: ArdDraftPayload = {
    savedAt,
    baseVersionId: baseArd?.versionId,
    formData,
  };
  save(key, payload);
  return savedAt;
};

/** Remove a draft from localStorage. No-op if `key` is `null`. */
export const clearDraft = (key: string | null): void => {
  if (!key) return;
  remove(key);
};
